import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const TopSkorPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [selectedCompetitionName, setSelectedCompetitionName] = useState('');
  const [scorers, setScorers] = useState([]);
  const [loadingScorers, setLoadingScorers] = useState(false);

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const fetchCompetitions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/lapangbola/competitions`);
      
      if (response.data && response.data.data && response.data.data.length > 0) {
        setCompetitions(response.data.data);
        const firstComp = response.data.data[0];
        setSelectedCompetition(firstComp.id);
        setSelectedCompetitionName(firstComp.name);
        fetchScorers(firstComp.id);
      } else {
        setCompetitions([]);
      }
    } catch (error) {
      console.error('Error fetching competitions:', error);
      toast.error('Gagal memuat data kompetisi');
    } finally {
      setLoading(false);
    }
  };

  const fetchScorers = async (competitionId) => {
    try {
      setLoadingScorers(true);
      const response = await axios.get(`${API}/api/lapangbola/topscorers`, {
        params: { competition_id: competitionId }
      });
      
      if (response.data && response.data.data) {
        setScorers(response.data.data);
      } else {
        setScorers([]);
      }
    } catch (error) {
      console.error('Error fetching top scorers:', error);
      toast.error('Gagal memuat data top skor');
      setScorers([]);
    } finally {
      setLoadingScorers(false);
    }
  };

  const handleCompetitionChange = (competitionId) => {
    const comp = competitions.find(c => c.id === Number(competitionId));
    setSelectedCompetition(Number(competitionId));
    setSelectedCompetitionName(comp?.name || '');
    fetchScorers(Number(competitionId));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ltb-bright mx-auto"></div>
          <p className="mt-4 text-gray-600">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-ltb-r text-white p-4">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="mb-2 flex items-center text-white/80 hover:text-white"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Kembali
          </button>
          <h1 className="text-2xl font-bold">Top Skor</h1>
          <p className="text-white/80 text-sm mt-1">Daftar pencetak gol terbanyak</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Competition Selector */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Pilih Kompetisi</label>
          <select
            value={selectedCompetition || ''}
            onChange={(e) => handleCompetitionChange(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
          >
            {competitions.map(comp => (
              <option key={comp.id} value={comp.id}>{comp.name}</option>
            ))}
          </select>
        </div>

        {/* Top Scorers List */}
        {loadingScorers ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ltb-bright mx-auto mb-4"></div>
            <p className="text-gray-600">Memuat data top skor...</p>
          </div>
        ) : scorers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <p className="text-gray-600 text-lg">Belum ada data top skor</p>
            <p className="text-gray-400 text-sm mt-2">Data akan ditampilkan setelah pertandingan berlangsung</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* Competition title */}
            {selectedCompetitionName && (
              <div className="bg-gradient-ltb-r text-white px-4 py-3">
                <h2 className="text-lg font-semibold">{selectedCompetitionName}</h2>
              </div>
            )}
            
            {/* Top 3 Podium */}
            {scorers.length >= 3 && (
              <div className="bg-gradient-to-b from-gray-50 to-white p-4">
                <div className="flex justify-center items-end space-x-4">
                  {/* 2nd Place */}
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-2 rounded-full border-4 border-gray-300 overflow-hidden bg-gray-200">
                      <img 
                        src={scorers[1]?.player_picture} 
                        alt={scorers[1]?.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23999"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>';
                        }}
                      />
                    </div>
                    <div className="bg-gray-300 text-gray-700 w-8 h-8 rounded-full mx-auto flex items-center justify-center font-bold text-sm">2</div>
                    <p className="text-xs font-medium mt-1 truncate max-w-[80px]">{scorers[1]?.name}</p>
                    <p className="text-lg font-bold text-ltb-bright">{scorers[1]?.total_goal}</p>
                  </div>
                  
                  {/* 1st Place */}
                  <div className="text-center -mt-4">
                    <div className="w-20 h-20 mx-auto mb-2 rounded-full border-4 border-yellow-400 overflow-hidden bg-gray-200 shadow-lg">
                      <img 
                        src={scorers[0]?.player_picture} 
                        alt={scorers[0]?.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23999"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>';
                        }}
                      />
                    </div>
                    <div className="bg-yellow-400 text-yellow-900 w-10 h-10 rounded-full mx-auto flex items-center justify-center font-bold">1</div>
                    <p className="text-sm font-medium mt-1 truncate max-w-[100px]">{scorers[0]?.name}</p>
                    <p className="text-xl font-bold text-ltb-bright">{scorers[0]?.total_goal}</p>
                  </div>
                  
                  {/* 3rd Place */}
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-2 rounded-full border-4 border-orange-300 overflow-hidden bg-gray-200">
                      <img 
                        src={scorers[2]?.player_picture} 
                        alt={scorers[2]?.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23999"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>';
                        }}
                      />
                    </div>
                    <div className="bg-orange-300 text-orange-800 w-8 h-8 rounded-full mx-auto flex items-center justify-center font-bold text-sm">3</div>
                    <p className="text-xs font-medium mt-1 truncate max-w-[80px]">{scorers[2]?.name}</p>
                    <p className="text-lg font-bold text-ltb-bright">{scorers[2]?.total_goal}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Full List Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-12">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Pemain</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase hidden sm:table-cell">Tim</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-12">M</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-16 bg-ltb-bright/10 text-ltb-bright">Gol</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {scorers.map((player, index) => (
                    <tr 
                      key={player.player_id || index} 
                      className={`hover:bg-gray-50 transition-colors ${index < 3 ? 'bg-yellow-50/30' : ''}`}
                    >
                      <td className="px-3 py-3 text-center">
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${
                          index === 0 ? 'bg-yellow-400 text-yellow-900' :
                          index === 1 ? 'bg-gray-300 text-gray-700' :
                          index === 2 ? 'bg-orange-300 text-orange-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {index + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                            <img 
                              src={player.player_picture} 
                              alt={player.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23999"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>';
                              }}
                            />
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-900">{player.name}</span>
                            <p className="text-xs text-gray-500 sm:hidden">{player.club_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <div className="flex items-center space-x-2">
                          <img 
                            src={player.club_picture} 
                            alt={player.club_name}
                            className="w-6 h-6 object-contain"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><circle cx="12" cy="12" r="10"/></svg>';
                            }}
                          />
                          <span className="text-sm text-gray-600">{player.club_name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm text-center text-gray-600 font-medium">{player.total_play || 0}</td>
                      <td className="px-3 py-3 text-center bg-ltb-bright/5">
                        <span className="text-lg font-bold text-ltb-bright">{player.total_goal}</span>
                        {player.total_penalty_goal > 0 && (
                          <span className="text-xs text-gray-500 block">({player.total_penalty_goal} pen)</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Info Footer */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-blue-800">
              <p className="font-medium">Keterangan:</p>
              <p className="mt-1">M = Jumlah Pertandingan | Gol = Total Gol | pen = Gol dari Penalti</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopSkorPage;
